import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaChevronRight, FaGripLines } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { RiImageAddLine } from "react-icons/ri";
import Pagination from "../../../../libs/Pagination";
import { addLanguage, deleteLanguage, getAllLanguages, updateLanguage, languageSequence } from "../../../../libs/api/languageApi";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../libs/LoadingSpinner";
import Swal from "sweetalert2";
import { AppContext } from "../../../../context/appContext";
import { useNavigate } from "react-router-dom";

export default function BibleLanguageList() {
  const { t, formatNum, isRTL } = useContext(AppContext);
  const navigate = useNavigate();
  const [languages, setLanguages] = useState([]);
  const [languageImage, setLanguageImage] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [direction, setDirection] = useState("ltr");
  const [selectedLanguageId, setSelectedLanguageId] = useState(null);
  const [errors, setErrors] = useState({ name: "", code: "", image: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  useEffect(() => {
    fetchLanguages(true);
  }, [currentPage, limit, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchLanguages = async (isLoading) => {
    setInitialLoad(isLoading);
    try {
      const response = await getAllLanguages(currentPage, limit, debouncedSearch);
      if (response.status === 200) {
        const languagesWithSequence = response.data.data.map((language, index) => ({
          ...language,
          sequence: language.sequence || index + 1
        }));
        setLanguages(languagesWithSequence || []);
        setTotalPages(response.data.totalPages || 1);
        setLimit(response.data.limit || 10);
        setCurrentPage(response.data.currentPage || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setInitialLoad(false);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value || !value.toString().trim()) {
          error = t('validation.languageNameRequired') || 'Language name is required';
        }
        break;
      case 'image':
        if (value && value.size && value.size > 25 * 1024 * 1024) {
          error = t('validation.imageSizeTooLarge');
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'name') setName(value);
    if (fieldName === 'code') setCode(value);
    if (fieldName === 'direction') setDirection(value);
    if (fieldName === 'image') setLanguageImage(value);

    const error = validateField(fieldName, value);
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: error }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    newErrors.name = validateField('name', name);
    setErrors(newErrors);

    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    const formData = new FormData();
    formData.append('name', name.trim());
    if (code && code.trim()) {
      formData.append('code', code.trim());
    }
    formData.append('direction', direction);
    if (languageImage) {
      formData.append('flag', languageImage);
    }

    try {
      setLoading(true);
      const response = await addLanguage(formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("language_modal").close();
        toast.success(response?.data?.message || t('messages.languageAddedSuccess') || 'Language added successfully');
        fetchLanguages(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    const newErrors = {};
    newErrors.name = validateField('name', name);
    setErrors(newErrors);

    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    const formData = new FormData();
    formData.append('name', name.trim());
    if (code && code.trim()) {
      formData.append('code', code.trim());
    }
    formData.append('direction', direction);
    if (languageImage && typeof languageImage !== 'string') {
      formData.append('flag', languageImage);
    }

    try {
      setLoading(true);
      const response = await updateLanguage(selectedLanguageId, formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("language_modal").close();
        toast.success(response?.data?.message || t('messages.languageUpdatedSuccess') || 'Language updated successfully');
        fetchLanguages(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t('messages.areYouSure'),
      text: t('bible.deleteLanguageWarning') || 'This will delete the language and all associated Bible books',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yesDelete'),
      cancelButtonText: t('common.cancel')
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await deleteLanguage(id);
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.data?.message || t('messages.languageDeletedSuccess') || 'Language deleted successfully');
        fetchLanguages(false);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openModal = (mode, language = null) => {
    setModalMode(mode);
    setSelectedLanguageId(null);
    if (mode === "edit" && language) {
      setName(language?.name || "");
      setCode(language?.code || "");
      setDirection(language?.direction || "ltr");
      setLanguageImage(language?.flag || null);
      setSelectedLanguageId(language?._id || null);
      setErrors({ name: "", code: "", image: "" });
    } else {
      setName("");
      setCode("");
      setDirection("ltr");
      setLanguageImage(null);
      setSelectedLanguageId(null);
      setErrors({ name: "", code: "", image: "" });
    }

    document.getElementById("language_modal").showModal();
  };

  const clearForm = () => {
    setName("");
    setCode("");
    setDirection("ltr");
    setLanguageImage(null);
    setSelectedLanguageId(null);
    setErrors({ name: "", code: "", image: "" });
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(languages[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    draggedElement.style.opacity = '0.3';

    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(languages[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = languages[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedLanguages = languages.map(language => {
      if (language._id === draggedItem._id) {
        return { ...language, sequence: targetSequence };
      }
      if (language._id === draggedOverItem._id) {
        return { ...language, sequence: draggedSequence };
      }
      return language;
    });

    // Sort by sequence to reflect the swap
    updatedLanguages.sort((a, b) => a.sequence - b.sequence);

    setLanguages(updatedLanguages);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedToUpdateSequence'));
      fetchLanguages(false);
    }
  };

  const handleDragEnd = (e) => {
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await languageSequence(payload);
      if (response.status === 200 || response.status === 201) {
        fetchLanguages(false);
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleNavigateToBooks = (languageId, languageName) => {
    navigate(`/admin/bible/books/${languageId}`, { state: { languageName } });
  };

  return (
    <div className="p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <h2 className="text-2xl font-semibold mb-4">{t('bible.title')}</h2>

      <div className="bg-white rounded-3xl shadow">
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('common.search')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2 border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('bible.addLanguage') || 'Add Language'}
          </button>
        </div>

        <div className="overflow-x-auto">
          {initialLoad && (
            <div className="flex justify-center items-center h-full p-6">
              <LoadingSpinner />
            </div>
          )}
          {!initialLoad && languages.length === 0 && (
            <div className="flex justify-center items-center h-full p-6">
              <p className="text-gray-500">{t('bible.noLanguages') || 'No languages found'}</p>
            </div>
          )}
          {!initialLoad && languages.length > 0 && (
            <div className="w-full">
              <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-7 border-b border-[#F7F7F9]">
                <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('bible.id')}</div>
                <div className="p-2 py-4">{t('language.flag') || 'Flag'}</div>
                <div className="p-2 py-4">{t('language.name') || 'Name'}</div>
                <div className="p-2 py-4">{t('language.code') || 'Code'}</div>
                <div className="p-2 py-4">{t('language.direction') || 'Direction'}</div>
                <div className="p-2 py-4">{t('bible.booksCount') || 'Books'}</div>
                <div className="p-2 py-4">{t('bible.action')}</div>
              </div>

              <div>
                {languages.map((language, index) => (
                  <div
                    key={language._id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`grid grid-cols-7 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                      draggedItem?._id === language._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                    } ${
                      dragOverItem?._id === language._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                    }`}
                    style={{
                      cursor: 'move',
                      transition: 'all 0.3s ease',
                      userSelect: 'none'
                    }}
                  >
                    <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                      <FaGripLines className="text-gray-400" />
                      {formatNum((currentPage - 1) * limit + index + 1)}
                    </div>
                    <div className="p-2 py-6">
                      {language?.flag ? (
                        <img src={language.flag} alt={language.name} className="w-12 h-8 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                          {t('common.na')}
                        </div>
                      )}
                    </div>
                    <div className="p-2 py-6 capitalize">{language?.name || "-"}</div>
                    <div className="p-2 py-6 uppercase">{language?.code || "-"}</div>
                    <div className="p-2 py-6 uppercase">{language?.direction || "ltr"}</div>
                    <div className="p-2 py-6">{formatNum(language?.bibleCount || 0)}</div>
                    <div className="p-2 py-6">
                      <div className="flex gap-4 items-center">
                        <button
                          draggable={false}
                          onClick={() => handleNavigateToBooks(language._id, language.name)}
                          className="text-green-600 hover:text-green-800 transition-colors cursor-pointer"
                          title={t('bible.viewBooks') || 'View Books'}
                        >
                          <FaChevronRight size={18} />
                        </button>
                        <BiEditAlt
                          size={24}
                          className="cursor-pointer hover:opacity-50 text-blue-600"
                          onClick={() => openModal("edit", language)}
                        />
                        <FaRegTrashCan
                          size={20}
                          className="cursor-pointer hover:opacity-50 text-red-500"
                          onClick={() => handleDelete(language._id)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!initialLoad && languages.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')}{" "}
              {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')} {formatNum(totalRecords)}{" "}
              {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <dialog
        id="language_modal"
        className="modal transition-all shadow-[0px_1px_2px_0px_#0A0D120F]"
      >
        <div className="modal-box min-w-[800px] rounded-2xl p-0" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add"
                  ? t('bible.addLanguage') || 'Add Language'
                  : t('bible.editLanguage') || 'Edit Language'}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Name */}
            <label className="block text-sm font-medium mb-2">{t('language.name') || 'Language Name'}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder={t('language.enterName') || 'Enter language name'}
              className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.name && (
              <p className="text-red-500 absolute -mt-[15px] text-xs">
                {errors.name}
              </p>
            )}

            {/* Code */}
            <label className="block text-sm font-medium mb-2 mt-2">{t('language.code') || 'Language Code'}</label>
            <input
              type="text"
              value={code}
              onChange={(e) => handleFieldChange('code', e.target.value)}
              placeholder={t('language.enterCode') || 'Enter language code (e.g., en, fa, ar)'}
              className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir="ltr"
            />
            {errors.code && (
              <p className="text-red-500 absolute -mt-[15px] text-xs">
                {errors.code}
              </p>
            )}

            {/* Direction */}
            <label className="block text-sm font-medium mb-2 mt-2">{t('language.direction') || 'Text Direction'}</label>
            <select
              className="w-full mb-4 border rounded-xl p-3 border-[#808080] bg-white"
              value={direction}
              onChange={(e) => handleFieldChange('direction', e.target.value)}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="ltr">{t('language.ltr') || 'Left to Right (LTR)'}</option>
              <option value="rtl">{t('language.rtl') || 'Right to Left (RTL)'}</option>
            </select>

            {/* Flag Image */}
            <div className="my-5 mt-1">
              <label className="block text-sm font-medium mb-2">
                {t('language.flag') || 'Flag/Image'}
              </label>
              <div
                className="relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith("image/")) {
                    if (file.size <= 25 * 1024 * 1024) {
                      setLanguageImage(file);
                      handleFieldChange('image', file);
                      setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
                    } else {
                      setErrors((prevErrors) => ({ ...prevErrors, image: t('validation.imageSizeTooLarge') }));
                    }
                  }
                }}
                onClick={() =>
                  document.getElementById("languageImageUploadInput").click()
                }
              >
                {languageImage ? (
                  <img
                    src={typeof languageImage === 'string' ? languageImage : URL.createObjectURL(languageImage)}
                    alt="language flag"
                    className="max-h-40 mx-auto mb-2"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                    <div className="bg-gray-100 rounded-full min-h-12 min-w-12 flex items-center justify-center">
                      <RiImageAddLine size={25} className="text-blue-500" />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-md text-blue-500 font-medium">
                        {t('forms.clickToUpload') || 'Click to upload'}
                      </p>
                      <p className="text-md text-black">{t('forms.orDragDrop') || 'or drag and drop'}</p>
                    </div>
                    <p className="text-sm text-black">
                      {t('forms.maxFileSize25MB') || 'Max file size: 25MB'}
                    </p>
                  </div>
                )}
                <input
                  id="languageImageUploadInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size <= 25 * 1024 * 1024) {
                        setLanguageImage(file);
                        handleFieldChange('image', file);
                        setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
                      } else {
                        setErrors((prevErrors) => ({ ...prevErrors, image: t('validation.imageSizeTooLarge') }));
                      }
                    }
                    e.target.value = '';
                  }}
                />
              </div>
            </div>
            {errors.image && <p className="text-red-500 absolute -mt-[18px] text-xs">{errors.image}</p>}
          </div>

          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loading ? (
                <LoadingSpinner />
              ) : modalMode === "add" ? (
                t('common.add')
              ) : (
                t('common.update')
              )}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
